import React, { useEffect, useMemo } from "react";
import DOMPurify from "dompurify";

import _ from "lodash";
import cx from "classnames";

import styles from "./view.module.scss";
import Button from "src/components/button";

import { BannerView } from "src/components/banner";
import { LearningRecordSimulation } from "src/types/coaching/learners";
import { SimulationContext } from "./context";
import { useTransition, animated } from "@react-spring/web";
import { CallSimulator } from "./simulator";
import { VapiContextProvider } from "./simulator/hook";
import { CALL_TYPES, DIFFICULTY_CHOICES } from "src/constants/simulation";

interface SimulationSummaryComponentProps {
  simulation: LearningRecordSimulation;
}

export const SimulationSummaryComponent: React.FC<SimulationSummaryComponentProps> = ({ simulation }) => {
  const simulationContext = React.useContext(SimulationContext);

  const summaryView = simulationContext.view === "summary";
  const session = useMemo(() => _.get(simulation, "session"), [simulation]);

  const viewTransition = useTransition(summaryView, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const callTransition = useTransition(!summaryView, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const handleStartSimulation = () => {
    if (simulationContext.startSimulation) {
      simulationContext.startSimulation();
    }
  }

  const handleViewReport = () => {
    if (simulationContext.viewReport) {
      simulationContext.viewReport();
    }
  }

  useEffect(() => {
    if (session) {
      if (simulationContext.changeView) {
        simulationContext.changeView("simulator");
      }
    }
  }, [session]);

  return (
    <div className={styles.simulationContainer}>
      {viewTransition(
        (style, item) =>
          item && (
            <animated.div style={style} className={styles.screen}>
              <SimulationView simulation={simulation} onStart={handleStartSimulation} onViewReport={handleViewReport} />
            </animated.div>
          ),
      )}

      {callTransition(
        (style, item) =>
          item && (
            <animated.div style={style} className={styles.screen}>
              <VapiContextProvider>
                <CallSimulator simulation={simulation} />
              </VapiContextProvider>
            </animated.div>
          ),
      )}
    </div>
  );
};

interface SimulationViewProps {
  simulation: LearningRecordSimulation;
  onStart: () => void;
  onViewReport: () => void;
}

const SimulationView: React.FC<SimulationViewProps> = ({ simulation, onStart, onViewReport }) => {
  const simulationContext = React.useContext(SimulationContext);

  const name = useMemo(() => _.get(simulation, "simulation.name") || "", [simulation]);
  const status = useMemo(() => _.get(simulation, "status"), [simulation]);
  const description = useMemo(() => _.get(simulation, "simulation.description") || "", [simulation]);
  const banner = useMemo(() => _.get(simulation, "simulation.banner"), [simulation]);

  const callType = useMemo(() => {
    const value = _.get(simulation, "simulation.type") || "";
    return CALL_TYPES.find((item) => item.id === value)?.title || "";
  }, [simulation]);

  const difficulty = useMemo(() => {
    const value = _.get(simulation, "simulation.difficulty") || "";
    return DIFFICULTY_CHOICES.find((item) => item.value === value)?.label || "";
  }, [simulation]);

  const handleBackClicked = () => {
    if (simulationContext.onBack) {
      simulationContext.onBack();
    }
  }

  const handleEnrollSimulation = () => {
    if (simulationContext.enrollSimulation) {
      simulationContext.enrollSimulation();
    }
  }

  const handleStartSimulation = () => {
    onStart();
  }

  const handleRestartSimulation = () => {
    simulationContext.restartSimulation?.();
  }

  const handleViewReports = () => {
    onViewReport();
  }

  const handleRetakeSimulation = () => {
    if (simulationContext.retakeSimulation) {
      simulationContext.retakeSimulation();
    }
  }

  return (
    <div className={styles.simulationViewContainer}>
      <div className={styles.left}>
        <div className={styles.simulationDetails}>
          {simulationContext.onBack && (
            <div className={styles.back} onClick={handleBackClicked}>
              <i className="paperflite-left-arrow"></i>
              <span>my courses</span>
            </div>
          )}

          <div className={styles.details}>
            <div className={styles.name}>{name}</div>
            <div className={styles.thumbnail}>
              <BannerView
                banner={banner as any}
                defaultBanner="/images/assets/default_asset.jpg"
              />
              <div className={styles.actions}>
                {status === "NOT_ENROLLED" && (
                  <Button
                    className={styles.startCourse}
                    text={simulationContext.enrollingSimulation ? "Enrolling..." : "Enroll Call"}
                    variant="gradient"
                    icon="paperflite-play"
                    disabled={simulationContext.enrollingSimulation}
                    onClick={handleEnrollSimulation}
                  />
                )}

                {status === "NOT_STARTED" && (
                  <Button
                    className={styles.startCourse}
                    text={simulationContext.startingSimulation ? "Starting..." : "Start Call"}
                    variant="gradient"
                    icon="paperflite-play"
                    disabled={simulationContext.startingSimulation}
                    onClick={handleStartSimulation}
                  />
                )}

                {status === "COMPLETED" && simulation?.report?.status !== "in_sufficient_duration" &&
                  <>
                    {simulation.report?.status === "completed" && (
                      <Button
                        className={styles.viewReports}
                        text={simulationContext.fetchingReport ? "Fetching report..." : "View Reports"}
                        variant="gradient"
                        icon="paperflite-analytics"
                        disabled={simulationContext.fetchingReport || simulationContext.retakingSimulation}
                        onClick={handleViewReports}
                      />
                    )}

                    {simulation.report?.status !== "completed" && (
                      <Button
                        className={styles.proceesingReports}
                        text={"Processing call..."}
                        variant="gradient"
                        icon="paperflite-analytics"
                      />
                    )}
                  </>
                }

                {status === "COMPLETED" && simulation?.report?.status == "in_sufficient_duration" &&
                  <Button
                    className={styles.viewReports}
                    text={simulationContext.restartingSimulation ? "Restarting..." : "Restart Simulation"}
                    variant="gradient"
                    icon="paperflite-refresh"
                    disabled={simulationContext.restartingSimulation}
                    onClick={handleRestartSimulation}
                  />
                }

                {status === "COMPLETED" && simulation?.report?.status !== "in_sufficient_duration" && (
                  <Button
                    className={styles.startCourse}
                    text={simulationContext.retakingSimulation ? "Retaking..." : "Retake Simulation"}
                    variant="gradient"
                    icon="paperflite-play"
                    disabled={simulationContext.retakingSimulation}
                    onClick={handleRetakeSimulation}
                  />
                )}
              </div>
            </div>

            <div className={styles.simulationMetadata}>
              <div className={styles.metadata}>
                <i className="paperflite-clock" />
                {`${simulation.simulation.duration} Minutes`}
              </div>
              <span className={styles.border} />
              <div className={styles.metadata}>
                <i className="paperflite-calendar" />
                {callType}
              </div>
              <span className={styles.border} />
              <div className={styles.metadata}>
                <i className="paperflite-speedomenter" />
                {difficulty}
              </div>
            </div>

            <div className={styles.description}>
              <p className={styles.title}>Description</p>
              <div
                className={styles.descriptionContent}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(description),
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.right}>
        {status === "COMPLETED" && (simulation?.score ?? 0) > 0 && (
          <div className={styles.scoreContainer}>
            <h6 className={styles.name}>Your Score</h6>
            <div className={styles.details}>
              <div className={styles.label}>
                here&apos;s your score for this simulation
              </div>
              <div className={styles.score}>
                {Math.min(Number(((simulation.score || 0) * 100).toFixed(2)), 100)}%
              </div>
            </div>
          </div>
        )}
        <div className={styles.property}>
          <h6 className={styles.name}>Organization Info</h6>
          <div className={styles.body}>
            <div className={styles.organization}>
              <div className={styles.name}>
                {simulation.simulation?.organization?.name || ""}
              </div>
              <div className={styles.description}>
                {simulation.simulation?.organization?.description || ""}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.property}>
          <h6 className={styles.name}>Persona</h6>
          <div className={styles.body}>
            <div className={styles.persona}>
              <div className={styles.name}>
                {simulation.simulation?.persona?.name || ""}
              </div>
              <div className={styles.description}>
                {simulation.simulation?.persona?.description || ""}
              </div>
            </div>
          </div>
        </div>
        <div className={styles.property}>
          <h6 className={styles.name}>Objectives</h6>
          <div className={styles.body}>
            <div className={styles.objectives}>
              <div className={styles.description}>
                {simulation.simulation?.objectives?.description || ""}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}